import React, { useEffect, useRef, useState } from 'react';
import { getWeekStatus, getInitialData, getDraftStatus } from './DatabaseAPI';

// Import new page components (to be created)
import DraftPage from './pages/DraftPage';
import TeamPage from './pages/TeamPage';
import PlayerPage from './pages/PlayerPage';
import StandingsPage from './pages/StandingsPage';
import GearPage from './pages/GearPage';
import LeaguePage from './pages/LeaguePage';

// Import debug components
import ComponentViewer from './dev-tools/ComponentViewer';

// SEASON TEAMS DO REACH THIS SHELL — via the draft.
//
// (An earlier revision of this comment claimed the draft path was "weekly-only"
// and that no mode gate was needed here. That was WRONG, and it hid a real dead
// end. AppRouter.handleTeamClick() routes on `needs_draft` with no mode check,
// so a SEASON team lands in PACK_SELECTION exactly like a weekly one.)
//
// AND IT OPENS EARLIER THAN ONCE DOCUMENTED (2026-08-17, PR review): a season
// draft is available from FORMING, not only after the commissioner starts the
// league. `serialize_league_dashboard()` sets `can_draft_initial_roster` for
// status in ('FORMING', 'DRAFTING'), and backend/tests/football/season/
// test_season_draft_to_regular_season.py:161-192 drafts the creator to
// completion while the league is still FORMING. So this shell can receive a
// season team immediately after create/join — before any commissioner action.
//
// The bug that caused: on completion SequentialPackSystem calls setPage(8) →
// GEAR, whose GearPage calls getTeamData() → the generic team-stats endpoint.
// TeamStatsView.get() runs assert_weekly_league() and REJECTS a season league,
// so a finished season draft dead-ended on an error screen with no route to its
// season roster.
//
// The fix, and why it is not a second source of truth: this shell does NOT
// re-derive the mode. It READS the value AppRouter already resolved from the
// backend's `mode` field and persisted in sessionStorage, and uses it only to
// decide "hand this team back to AppRouter's season-aware route" vs "render the
// weekly page as before". The decision still belongs to AppRouter; this is a
// hand-off, not a duplicate gate.
// WHY THIS ONE DOES *NOT* THROW ON A MISSING VALUE, unlike the two guards in
// AppRouter (handleTeamClick and the 'teamPage' render branch, which both reject
// an absent mode as a contract failure):
//
// Those guards sit on paths that ALWAYS originate from a `/auth/teams/` row, so
// a missing mode there is genuinely impossible-unless-broken. This shell is also
// mounted from dev-tools/ComponentViewer and the legacy 'newTeam'/'newDraft'
// modes, where no team was ever clicked and nothing wrote the key — absence is
// ORDINARY there, not corruption.
//
// The asymmetry is safe because of what this value is used for here: it only
// decides whether to HAND OFF to AppRouter. Reading absent as "not season" makes
// the shell behave exactly as it did before season mode existed — the weekly
// page renders and produces its own honest errors if the data is wrong. It
// cannot mis-route a season team, because a real season team always arrives with
// the key set by handleTeamClick.
const resolveSeasonMode = () => sessionStorage.getItem('selectedLeagueMode') === 'SEASON';

// Re-use the same PAGES constants as MythicalFootball.js for consistency
export const NEW_PAGES = {
    "LOGIN": 0,
    "TEAM": 1,
    "CHARACTER": 2,
    "MATCHUP": 3,
    "STANDING": 4,
    "DRAFT": 5,
    "PACK_SELECTION": 6,
    "PACK_OPENING": 7,
    "GEAR": 8,
    "LEAGUE": 9,
    "DEBUG": 99
}

// Every page in this list is built on weekly-only endpoints that
// assert_weekly_league() rejects for a season league. Module scope, not
// component scope: it is a constant, and the hand-off effect below lists it as
// a dependency — rebuilding the array each render would re-fire that effect on
// every render.
// GEAR IS DELIBERATELY ABSENT FROM THIS LIST (2026-08-28).
//
// It was listed while `serialize_roster()` omitted `character_sheet_id` — gear
// is keyed on the CharacterSheet end to end, so without that id every season
// player collapsed into one `undefined` bucket and equipped gear matched no
// bucket at all, vanishing from the page. That was worse than not offering the
// screen, so season teams were handed back instead.
//
// The serializer now returns `character_sheet_id`
// (backend/football/season/serializers.py), and the gear mutation endpoints are
// season-aware (backend/game/api/views/gear.py runs
// assert_generic_surface_authorized_for_season + SeasonMutationGuard, and has
// no assert_weekly_league anywhere). Gear is shared Team inventory in both
// modes, so the page now works for a season roster and the gate is gone.
//
// Do NOT re-add GEAR here without first confirming the roster contract actually
// stopped returning the id.
const SEASON_UNSUPPORTED_PAGES = [
    NEW_PAGES.TEAM,
    NEW_PAGES.CHARACTER,
    NEW_PAGES.STANDING,
    NEW_PAGES.MATCHUP,
    NEW_PAGES.LEAGUE,
];

function NewApp({ initialPage, user: initialUser, leagueId: initialLeagueId, leagueName: initialLeagueName, onComplete }) {
    // Set initial page based on props (TEAM or DRAFT)
    const [page, setPage] = useState(initialPage ? NEW_PAGES[initialPage] : NEW_PAGES.TEAM);
    const [user, setUser] = useState(initialUser || null);
    const [leagueId, setLeagueId] = useState(initialLeagueId || null);
    const [leagueName, setLeagueName] = useState(initialLeagueName || null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [selectedOpponentTeam, setSelectedOpponentTeam] = useState(null);
    const [weekStatus, setWeekStatus] = useState(null);
    
    // Draft-specific state
    const [selectedPackType, setSelectedPackType] = useState(null); // 'PLAYER' or 'GEAR'
    const [draftData, setDraftData] = useState(null); // Draft session data

    let PageComponent;
    let pageProps = {};

    // Handle TeamPage navigation - convert string destinations to NEW_PAGES constants.
    //
    // This handler is also the `onNavigate` given to PlayerPage inside this shell
    // (see NEW_PAGES.CHARACTER below), so it must accept PlayerPage's vocabulary
    // too: 'LEAGUES' and 'STANDINGS' in addition to the TeamPage/GearPage words.
    //
    // THIS IS THE 2026-09-20 "EQUIP does nothing" BUG. PlayerPage used to decide
    // which vocabulary to emit by checking whether `sessionStorage.selectedTeamId`
    // was set, treating "set" as "I'm mounted by AppRouter" — but AppRouter writes
    // that key in handleTeamClick BEFORE it mounts this shell, so a PlayerPage
    // inside the post-draft shell sent AppRouter's lowercase 'gearPage' here, hit
    // the default branch, and the GEAR / EQUIP buttons did nothing at all.
    // PlayerPage now always emits the uppercase set and both hosts map it.
    const handleTeamPageNavigation = async (destination) => {
        console.log('NewApp: handleTeamPageNavigation called with:', destination);
        console.log('NewApp: Current page:', page);
        console.log('NewApp: initialPage:', initialPage);
        switch(destination) {
            case 'LEAGUES':
                // PlayerPage's LEAGUES tab == TeamPage's HOME: leave the team
                // context entirely and hand control back to the caller.
                sessionStorage.removeItem('selectedPlayerId');
                setSelectedPlayer(null);
                if (onComplete) {
                    onComplete();
                } else {
                    setPage(NEW_PAGES.LOGIN);
                }
                break;
            case 'HOME':
                // Context-aware navigation: if we started from draft, check if it's still active
                if (initialPage === 'DRAFT' && user?.id) {
                    try {
                        const draftStatus = await getDraftStatus(user.id);
                        console.log('NewApp: Draft status check:', draftStatus);
                        
                        // If draft exists and is not complete, return to draft
                        if (draftStatus.has_draft && !draftStatus.draft_state?.is_draft_complete) {
                            console.log('NewApp: Returning to active draft');
                            setPage(NEW_PAGES.DRAFT);
                            return;
                        }
                    } catch (error) {
                        console.error('Error checking draft status:', error);
                    }
                }
                // Go back to login/calling app
                if (onComplete) {
                    onComplete();
                } else {
                    setPage(NEW_PAGES.LOGIN);
                }
                break;
            case 'TEAM':
                // Coming back from a player sheet: clear the selection first, or
                // the `selectedPlayer` effect below immediately bounces us back to
                // CHARACTER and the TEAM tab looks dead.
                sessionStorage.removeItem('selectedPlayerId');
                setSelectedPlayer(null);
                setPage(NEW_PAGES.TEAM);
                break;
            case 'LEAGUE':
            case 'STANDINGS':
                // 'LEAGUE' from TeamPage/GearPage, 'STANDINGS' from PlayerPage —
                // same weekly standings screen.
                setSelectedPlayer(null);
                setPage(NEW_PAGES.STANDING);
                break;
            case 'GEAR':
                // `selectedPlayer` is deliberately NOT cleared here: GearPage
                // takes it as a prop to expand the player you came from, and
                // leaving it untouched also means the effect keyed on it does not
                // re-fire and bounce us back to CHARACTER.
                setPage(NEW_PAGES.GEAR);
                break;
            case 'PLAYER':
                console.log('NewApp: PLAYER navigation - navigating to character page');
                setSelectedPlayer(sessionStorage.getItem('selectedPlayerId'));
                setPage(NEW_PAGES.CHARACTER);
                break;
            default:
                // LOUD, not a debug log. This branch is exactly where the
                // 2026-09-20 dead-EQUIP bug lived: a `console.log` here reads as
                // ordinary noise, so a destination this shell cannot route
                // looked identical to normal operation while the button did
                // nothing at all.
                console.error(
                    'Unknown navigation destination in NewApp:', destination,
                    "- expected one of 'LEAGUES', 'HOME', 'TEAM', 'LEAGUE', 'STANDINGS', 'GEAR', 'PLAYER'."
                );
                break;
        }
    };

    // Handle gear navigation (when clicking on individual player gear)
    const handleGearNavigation = (playerId) => {
        console.log('NewApp: Gear navigation for player:', playerId);
        setSelectedPlayer(playerId);
        setPage(NEW_PAGES.GEAR);
    };

    // Initial data loading - same pattern as MythicalFootball.js
    useEffect(() => {
        getInitialData()
            .then(() => {
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error fetching initial data:', error);
                setIsLoading(false);
            });
    }, []);

    // Separate effect to fetch week status when leagueId changes OR when
    // navigating to team/gear pages.
    //
    // Skipped for a SEASON team: WeekStatusView returns the weekly
    // `League.current_week`/`stats_active` pair, which does not govern a season
    // league's lifecycle (SeasonWeek/SeasonLeagueConfig do). Passing that as
    // `weekStatus` into GearPage would let an irrelevant field decide whether
    // gear is editable. Left unset, GearPage falls through to server
    // arbitration — the same stance it takes on the AppRouter route.
    useEffect(() => {
        if (resolveSeasonMode()) {
            return;
        }
        if (leagueId && (page === NEW_PAGES.TEAM || page === NEW_PAGES.GEAR || page === NEW_PAGES.STANDING)) {
            getWeekStatus(leagueId)
                .then(status => {
                    console.log('MythicFootball: Loaded weekStatus:', status);
                    setWeekStatus(status);
                })
                .catch(error => {
                    console.error('Error fetching week status:', error);
                });
        }
    }, [leagueId, page]);

    // Handle selected player navigation
    useEffect(() => {
        if (selectedPlayer) {
            setPage(NEW_PAGES.CHARACTER);
        }
    }, [selectedPlayer]);

    // SEASON HAND-OFF — see the block comment above resolveSeasonMode() for WHY
    // this exists. This effect is the WHEN.
    //
    // It must be an effect, not a render-time call. `onComplete()` updates
    // several AppRouter states, and invoking it while THIS component renders is
    // the "Cannot update a component while rendering a different component"
    // violation: React is mid-render on NewApp and gets told to re-render its
    // parent. Beyond the warning it is genuinely unsafe — render is not
    // guaranteed to run once, so a discarded render, a StrictMode double-invoke,
    // or any render retry would fire the hand-off again.
    //
    // `handedOffRef` makes it idempotent regardless. StrictMode deliberately
    // mounts → unmounts → remounts, running this effect twice; the ref survives
    // that (refs persist across the remount) so the parent is updated exactly
    // once. It is intentionally never reset — once we've handed control back,
    // this shell instance has nothing further to do.
    const handedOffRef = useRef(false);
    const isSeasonTeam = resolveSeasonMode();
    const shouldHandOffToSeason =
        !isLoading &&
        isSeasonTeam &&
        Boolean(onComplete) &&
        SEASON_UNSUPPORTED_PAGES.includes(page);

    useEffect(() => {
        if (!shouldHandOffToSeason || handedOffRef.current) {
            return;
        }
        handedOffRef.current = true;
        console.log(
            `🏆 NewApp: season team reached weekly-only page ${page} — handing back ` +
            'to AppRouter for season-aware routing (SeasonTeamPage)'
        );
        onComplete();
    }, [shouldHandOffToSeason, page, onComplete]);

    // Handle selected opponent team navigation
    useEffect(() => {
        if (selectedOpponentTeam) {
            setPage(NEW_PAGES.CHARACTER);
        }
    }, [selectedOpponentTeam]);

    if (isLoading) {
        return <div>Loading New UI...</div>;
    }

    // SEASON HAND-OFF, render half — PURE. No side effect here.
    //
    // The effect above performs the actual hand-off; this only decides what to
    // paint in the frame(s) between "we know we're leaving" and AppRouter
    // actually swapping us out. Without it the weekly page would render for one
    // frame and fire its weekly-only requests — which is the very dead end the
    // hand-off exists to prevent.
    //
    // Note this deliberately reads the same `shouldHandOffToSeason` the effect
    // is keyed on, so the two halves can never disagree.
    if (shouldHandOffToSeason) {
        return <div>Returning to your campaign…</div>;
    }

    // Assign the daily TeamPage for a roster screen. Only reached for WEEKLY
    // teams — a season team is handed back to AppRouter above.
    const assignTeamPage = ({ teamId, viewMode, extraProps = {} }) => {
        PageComponent = TeamPage;
        pageProps = {
            teamId,
            setPage: handleTeamPageNavigation,
            setSelectedPlayer: handleGearNavigation,
            leagueId,
            leagueName,
            weekStatus,
            viewMode,
            ...extraProps
        };
    };

    switch (page) {
        case NEW_PAGES.LOGIN:
            // HOME button clicked - use callback if available, otherwise reload
            if (onComplete) {
                onComplete();
                return <div>Returning to teams...</div>;
            } else {
                window.location.reload();
                return <div>Returning to login...</div>;
            }
        case NEW_PAGES.TEAM:
        default:
            // This is the branch the post-draft path lands on
            // (SequentialPackSystem -> GEAR -> GearPage TEAM button).
            assignTeamPage({ teamId: user?.id, viewMode: 'own' });
            break;
        case NEW_PAGES.CHARACTER:
            // Handle both player character sheets and opponent team viewing
            if (selectedOpponentTeam) {
                // Viewing opponent team
                assignTeamPage({
                    teamId: selectedOpponentTeam?.id,
                    viewMode: 'opponent',
                    extraProps: {
                        opponentTeam: selectedOpponentTeam,
                        originalUser: user
                    }
                });
            } else if (selectedPlayer) {
                // Individual player page
                PageComponent = PlayerPage;
                pageProps = {
                    playerId: selectedPlayer,
                    teamId: user?.id,
                    leagueId,
                    onNavigate: handleTeamPageNavigation,
                    weekStatus,
                    viewMode: 'own'
                };
            } else {
                // Fallback to team page if neither is set
                assignTeamPage({ teamId: user?.id, viewMode: 'own' });
            }
            break;
        case NEW_PAGES.MATCHUP: 
            // For now, redirect to League page which will show matchup info
            PageComponent = LeaguePage;
            pageProps = {user, setPage, leagueId, leagueName, view: 'matchup'};
            break;
        case NEW_PAGES.STANDING:
            PageComponent = StandingsPage;
            pageProps = {
                user,
                setPage: (destination) => {
                    // Convert string destinations from StandingsPage to NEW_PAGES constants for MythicFootball
                    console.log('MythicFootball: Converting StandingsPage navigation:', destination);
                    switch(destination) {
                        case 'HOME':
                            setPage(NEW_PAGES.LOGIN);
                            break;
                        case 'TEAM':
                            setPage(NEW_PAGES.TEAM);
                            break;
                        case 'LEAGUE':
                            // Already on standings page
                            break;
                        case 'GEAR':
                            setPage(NEW_PAGES.GEAR);
                            break;
                        default:
                            // If it's already a number (fallback), use it directly
                            if (typeof destination === 'number') {
                                setPage(destination);
                            } else {
                                console.log('Unknown navigation destination in MythicFootball:', destination);
                            }
                            break;
                    }
                },
                leagueId,
                leagueName,
                setSelectedOpponentTeam
            };
            break;
        case NEW_PAGES.DRAFT:
            // Use new Sequential Pack System
            PageComponent = DraftPage;
            pageProps = {user, setPage, leagueId, leagueName};
            break;
        case NEW_PAGES.PACK_SELECTION:
            // Redirect to new draft system
            PageComponent = DraftPage;
            pageProps = {user, setPage, leagueId, leagueName};
            break;
        case NEW_PAGES.PACK_OPENING:
            // Redirect to new draft system
            PageComponent = DraftPage;
            pageProps = {user, setPage, leagueId, leagueName};
            break;
        case NEW_PAGES.GEAR:
            PageComponent = GearPage;
            pageProps = {user, setPage: handleTeamPageNavigation, leagueId, leagueName, selectedPlayer, setSelectedPlayer, weekStatus};
            break;
        case NEW_PAGES.LEAGUE:
            PageComponent = LeaguePage;
            pageProps = {user, setPage, leagueId, leagueName};
            break;
        case NEW_PAGES.DEBUG:
            PageComponent = ComponentViewer;
            pageProps = {setPage, user, leagueId, leagueName};
            break;
    }

    return (
        <div className="new-app">
            <PageComponent {...pageProps} />
        </div>
    );
}

export default NewApp;